import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import './StatusPage.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

// VERSION TRACKING
const APP_VERSION = "2.2";

interface HealthStats {
  uptime_percentage: number;
  average_response_ms: number;
  total_checks: number;
}

interface Component {
  status: string;
  response_time_ms?: number;
}

interface HistoryPoint {
  timestamp: string;
  uptime_percentage: number;
  average_response_ms: number;
  total_checks: number;
  status: string;
}

interface Incident {
  timestamp: string;
  status: string;
  response_time_ms: number;
}

interface HealthDashboard {
  current_status: {
    status: string;
    database: string;
    response_time_ms: number;
  };
  uptime_stats: {
    '1h': HealthStats;
    '24h': HealthStats;
    '7d': HealthStats;
    '30d': HealthStats;
  };
  history_24h: HistoryPoint[];
  recent_incidents: Incident[];
  components: {
    backend: Component;
    database: Component;
    api: Component;
  };
}

const parseServerDate = (s: string) => {
  if (/[zZ]$|[+-]\d\d:\d\d$/.test(s)) return new Date(s);
  return new Date(`${s}Z`);
};

// EXPLICIT SYMBOL FUNCTIONS
const getStatusSymbol = (status: string): string => {
  const normalized = status.toLowerCase().trim();
  
  if (normalized === 'down') return '';
  if (normalized === 'degraded') return '';
  if (normalized === 'healthy' || normalized === 'operational') return '';
  
  return '?';
};

const StatusPage: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [dashboard, setDashboard] = useState<HealthDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());

  useEffect(() => {
    loadHealthData();
    const interval = setInterval(loadHealthData, 60000);
    return () => clearInterval(interval);
  }, []);

  const loadHealthData = async () => {
    try {
      const response = await axios.get(`${API_URL.replace('/api', '')}/health/dashboard`);
      setDashboard(response.data);
      setLastUpdate(new Date());
      setError('');
    } catch (err: any) {
      setError('Failed to load health data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    if (status === 'healthy' || status === 'operational') return 'status-healthy';
    if (status === 'degraded') return 'status-degraded';
    return 'status-down';
  };

  const getUptimeColor = (percentage: number) => {
    if (percentage >= 99.9) return '#10b981';
    if (percentage >= 99) return '#84cc16';
    if (percentage >= 95) return '#eab308';
    if (percentage >= 90) return '#f97316';
    return '#ef4444';
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const formatUptime = (percentage: number) => {
    return `${percentage.toFixed(2)}%`;
  };

  if (loading) {
    return (
      <div className="status-page">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading system status...</p>
        </div>
      </div>
    );
  }

  if (!dashboard) {
    return (
      <div className="status-page">
        <div className="error-container">
          <p>{error || 'No health data available'}</p>
        </div>
      </div>
    );
  }

  const overallStatus = dashboard.current_status.status;

  return (
    <div className="status-page">
      <nav className="status-nav">
        <div className="nav-left">
          <h1> System Status</h1>
          <span className="version-badge">v{APP_VERSION}</span>
          <span className="last-update">Updated: {lastUpdate.toLocaleTimeString()}</span>
        </div>
        <div className="nav-right">
          {user && (
            <>
              <button onClick={() => navigate('/dashboard')}> Dashboard</button>
              <button onClick={logout}>Logout</button>
            </>
          )}
        </div>
      </nav>

      <div className="status-container">
        {/* Overall Status Banner */}
        <div className={`status-banner ${getStatusColor(overallStatus)}`}>
          <div className="status-icon">
            {getStatusSymbol(overallStatus)}
          </div>
          <div className="status-info">
            <h2>
              {overallStatus === 'healthy' ? 'All Systems Operational' :
                overallStatus === 'degraded' ? 'Partial System Outage' :
                  'System Outage'}
            </h2>
            <p>Response time: {dashboard.current_status.response_time_ms}ms</p>
          </div>
        </div>

        {/* Uptime Statistics */}
        <div className="uptime-grid">
          {Object.entries(dashboard.uptime_stats).map(([period, stats]) => (
            <div key={period} className="uptime-card">
              <div className="uptime-header">
                <span className="uptime-period">{period.toUpperCase()}</span>
                <span
                  className="uptime-percentage"
                  style={{ color: getUptimeColor(stats.uptime_percentage) }}
                >
                  {formatUptime(stats.uptime_percentage)}
                </span>
              </div>
              <div className="uptime-bar-container">
                <div
                  className="uptime-bar"
                  style={{
                    width: `${stats.uptime_percentage}%`,
                    backgroundColor: getUptimeColor(stats.uptime_percentage)
                  }}
                ></div>
              </div>
              <div className="uptime-details">
                <span>Avg Response: {stats.average_response_ms.toFixed(0)}ms</span>
                <span>{stats.total_checks} checks</span>
              </div>
            </div>
          ))}
        </div>

        {/* Components Status */}
        <div className="components-section">
          <h3>System Components</h3>
          <div className="components-grid">
            {Object.entries(dashboard.components).map(([name, component]) => (
              <div key={name} className={`component-card ${getStatusColor(component.status)}`}>
                <div className="component-icon">
                  {getStatusSymbol(component.status)}
                </div>
                <div className="component-info">
                  <h4>{name.charAt(0).toUpperCase() + name.slice(1)}</h4>
                  <p className="component-status">{component.status}</p>
                  {component.response_time_ms !== undefined && (
                    <p className="component-response">{component.response_time_ms}ms</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 24 Hour History */}
        {dashboard.history_24h.length > 0 && (() => {
          const now = new Date();
          const currentMinute = new Date(now);
          currentMinute.setSeconds(0, 0);
          
          const currentPeriodStart = new Date(currentMinute);
          const currentMinutes = currentMinute.getMinutes();
          const periodStartMinute = Math.floor(currentMinutes / 15) * 15;
          currentPeriodStart.setMinutes(periodStartMinute, 0, 0);
          
          const end = new Date(currentPeriodStart.getTime() + 15 * 60 * 1000);
          const start = new Date(end.getTime() - 24 * 60 * 60 * 1000);
          
          const numColumns = 96;
          const columns: Array<{ 
            startTime: Date;
            cells: Array<{ time: Date; check: HistoryPoint | null }> 
          }> = [];
          
          for (let colIdx = 0; colIdx < numColumns; colIdx++) {
            const colStartTime = new Date(start.getTime() + colIdx * 15 * 60 * 1000);
            const cells: Array<{ time: Date; check: HistoryPoint | null }> = [];
            for (let minuteOffset = 0; minuteOffset < 15; minuteOffset++) {
              const cellTime = new Date(colStartTime.getTime() + minuteOffset * 60 * 1000);
              cells.push({ time: cellTime, check: null });
            }
            columns.push({ startTime: colStartTime, cells });
          }
          
          for (const point of dashboard.history_24h) {
            const checkTime = parseServerDate(point.timestamp);
            if (checkTime < start || checkTime >= end) continue;
            
            const minutesSinceStart = Math.floor((checkTime.getTime() - start.getTime()) / (60 * 1000));
            if (minutesSinceStart < 0) continue;
            
            const colIdx = Math.floor(minutesSinceStart / 15);
            if (colIdx >= numColumns) continue;
            
            const cellIdx = minutesSinceStart % 15;
            if (cellIdx >= 15) continue;
            
            columns[colIdx].cells[cellIdx].check = point;
          }
          
          return (
            <div className="history-section">
              <h3>Last 24 Hours (1-minute resolution) - Current: {currentMinute.toLocaleTimeString()}</h3>
              
              <div className="history-grid-wrapper">
                {[14, 13, 12, 11, 10, 9, 8, 7, 6, 5, 4, 3, 2, 1, 0].map((rowNum) => (
                  <div key={rowNum} className="history-row">
                    {columns.map((column, colIdx) => {
                      const cell = column.cells[rowNum];
                      const check = cell.check;
                      const isCurrent = now >= cell.time && now < new Date(cell.time.getTime() + 60 * 1000);
                      const isFuture = cell.time > currentMinute;
                      
                      let barColor = '#e5e7eb';
                      let symbolText = '';
                      
                      if (isFuture) {
                        barColor = '#f3f4f6';
                      } else if (check) {
                        if (check.uptime_percentage === 100) {
                          barColor = '#10b981';
                        } else if (check.uptime_percentage >= 50) {
                          barColor = '#f59e0b';
                          symbolText = '';
                        } else {
                          barColor = '#ef4444';
                          symbolText = '';
                        }
                      } else if (isCurrent) {
                        barColor = '#cbd5e1';
                      }
                      
                      const title = isFuture 
                        ? `${cell.time.toLocaleTimeString()}  Future`
                        : check
                        ? `${cell.time.toLocaleTimeString()}  ${formatUptime(check.uptime_percentage)}  ${check.average_response_ms.toFixed(0)}ms`
                        : `${cell.time.toLocaleTimeString()}  No data`;
                      
                      return (
                        <div
                          key={colIdx}
                          className={`history-cell ${!check && !isFuture ? 'no-data' : ''} ${isCurrent ? 'current' : ''} ${isFuture ? 'future' : ''}`}
                          style={{ backgroundColor: barColor }}
                          title={title}
                        >
                          {symbolText && <span className="symbol-text">{symbolText}</span>}
                        </div>
                      );
                    })}
                  </div>
                ))}
                
                <div className="history-labels">
                  {columns.map((column, idx) => {
                    const minute = column.startTime.getMinutes();
                    const hour = column.startTime.getHours();
                    
                    if (minute === 0 && hour % 3 === 0) {
                      return <div key={idx} className="time-label">{String(hour).padStart(2, '0')}</div>;
                    }
                    
                    return <div key={idx} className="label-spacer" />;
                  })}
                </div>
              </div>
              
              <div className="history-legend">
                <div className="legend-items">
                  <span className="legend-item">
                    <span className="legend-symbol" style={{ backgroundColor: '#10b981' }}></span>
                    Healthy (100%)
                  </span>
                  <span className="legend-item">
                    <span className="legend-symbol" style={{ backgroundColor: '#f59e0b' }}></span>
                    Degraded (50-99%)
                  </span>
                  <span className="legend-item">
                    <span className="legend-symbol" style={{ backgroundColor: '#ef4444' }}></span>
                    Down (&lt;50%)
                  </span>
                  <span className="legend-item">
                    <span className="legend-symbol" style={{ backgroundColor: '#e5e7eb' }}></span>
                    No data
                  </span>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Recent Incidents - GUARANTEED SYMBOLS */}
        {dashboard.recent_incidents.length > 0 && (
          <div className="incidents-section">
            <h3>Recent Incidents</h3>
            <div className="incidents-list">
              {dashboard.recent_incidents.map((incident, index) => {
                const statusClass = incident.status === 'down' ? 'status-down' 
                  : incident.status === 'degraded' ? 'status-degraded' 
                  : 'status-healthy';
                
                const symbol = getStatusSymbol(incident.status);
                
                return (
                  <div key={index} className={`incident-item ${statusClass}`}>
                    <div className={`incident-circle ${statusClass}`}>
                      <span className="incident-symbol">{symbol}</span>
                    </div>
                    <div className="incident-info">
                      <p className="incident-status">{incident.status}</p>
                      <p className="incident-time">{formatDate(incident.timestamp)}</p>
                      {incident.response_time_ms !== null && incident.response_time_ms !== undefined && (
                        <p className="incident-detail">Response: {incident.response_time_ms}ms</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {dashboard.recent_incidents.length === 0 && (
          <div className="no-incidents">
            <p> No incidents in the past 7 days!</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default StatusPage;